/* eslint-disable max-lines-per-function */
import {
  Typography,
  Grid,
  Divider,
  Box,
  FormControlLabel,
  Switch,
  Accordion,
  AccordionSummary,
  Link,
  AccordionDetails,
  Button,
  Input,
  IconButton,
} from "@mui/material";
import React, {
  RefObject,
  ChangeEvent,
  useRef,
  useContext,
  useState,
  useMemo,
  useEffect,
} from "react";
import {
  ICarrierChanged,
  IOwnLogisticsCredentials,
  IOwnLogisticsMatrix,
  TCSVUploadStatus,
} from "../../interfaces/configuration";
import { UserContext } from "../../contexts/userContext";
import {
  getOwnFleetMatricesToShow,
  showInputErrors,
  validateMatrixFile,
} from "../../utils/credentials";
import { mongoTimeStampToString } from "../../utils/orders";
import OwnFleetRulesModal from "../modals/OwnFleetRulesModal";
import { getMatrixDownloadURL } from "../../requests/configurationRequests";
import {
  COUNTRIES_WITH_GEOGRAPHIC_TYPE,
  DEFAULT_GEOGRAPHIC_TYPE,
  EXAMPLE_MATRIX_ID,
} from "../../utils/constants";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { Settings } from "@mui/icons-material";
import {
  csvStatusLabelMap,
  getCSVUploadStatus,
} from "../../utils/configuration";
import OwnFleetApiKey from "./OwnFleetApiKey";
import config from "../../config/config";

const { AVENIDA_EVENT_ACTIVE } = config;
const FileInput = ({
  fileInputRef,
  handleClick,
  errors,
  onFileSelected,
  disabled,
}: {
  fileInputRef: RefObject<HTMLInputElement>;
  handleClick: () => void;
  errors: string[];
  onFileSelected: (_file: File | undefined) => void;
  disabled: boolean;
}) => (
  <>
    <Box
      className="m-t-10 br-5 b-muted p-10"
      style={{ backgroundColor: "white" }}
      position="relative"
      display="flex"
      alignItems="center"
    >
      <Input
        type="file"
        inputRef={fileInputRef}
        inputProps={{ className: "f-s-14", accept: ".csv" }}
        sx={{
          position: "absolute",
          paddingLeft: "137px",
          pointerEvents: "none",
          "& input::-webkit-file-upload-button, &::after, &::before": {
            display: "none",
          },
        }}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          onFileSelected(e.target.files?.[0])
        }
        disabled={disabled}
      />
      <Button
        variant="contained"
        sx={{ width: 117, height: 37 }}
        onClick={handleClick}
        disabled={!!AVENIDA_EVENT_ACTIVE || disabled}
      >
        Importar CSV
      </Button>
    </Box>
    {errors.length > 0 && showInputErrors(errors)}
  </>
);

const CSVUploadStatusLabel = ({
  csvStatus,
}: {
  csvStatus: TCSVUploadStatus;
}) => {
  return (
    <Typography
      className="f-s-14 m-t-10"
      color={csvStatusLabelMap[csvStatus].color}
    >
      {csvStatusLabelMap[csvStatus].label}
    </Typography>
  );
};

export const OwnFleetSettings = ({
  ownFleetCarrier,
  handleCarriersChanges,
  onMatrixValidationChange,
}: {
  ownFleetCarrier: any;
  handleCarriersChanges: (_carrier: ICarrierChanged) => void;
  onMatrixValidationChange?: (_hasError: boolean) => void;
}) => {
  const { currentUser, selectedMarketplace } = useContext(UserContext);

  const currentMatrix = useMemo(() => {
    if (
      !ownFleetCarrier?.credentials?.matrices ||
      ownFleetCarrier?.credentials?.matrices.length === 0
    )
      return [];
    return getOwnFleetMatricesToShow(ownFleetCarrier?.credentials?.matrices);
  }, [ownFleetCarrier?.credentials?.matrices]);
  const csvStatus = getCSVUploadStatus(currentMatrix);
  const isProcessing = csvStatus === "in-process";

  // AL Pepe. SACARLO.
  const [checked, setChecked] = useState<boolean>(!ownFleetCarrier?.disabled);

  const canEnableOwnFleet = ownFleetCarrier?.credentials?.matrices?.some(
    (m: any) => m.active
  );

  const [showRulesModal, setShowRulesModal] = useState(false);
  const [expandAccordion, setExpandAccordion] = useState(false);
  const [exampleMatrixUrl, setExampleMatrixUrl] = useState("" as string);
  const [matrixFileErrors, setMatrixFileErrors] = useState<string[]>([]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const defaultGeoType = COUNTRIES_WITH_GEOGRAPHIC_TYPE.includes(
    currentUser?.entityInfo?.country ?? ""
  )
    ? DEFAULT_GEOGRAPHIC_TYPE
    : undefined;

  const handleClick = () => fileInputRef.current?.click();
  const handleChangeCredentials = (
    newCredentials: IOwnLogisticsCredentials
  ) => {
    handleCarriersChanges({
      ...ownFleetCarrier,
      _id: ownFleetCarrier?.carrier_id,
      credentials: {
        client_id: Number(ownFleetCarrier?.client_id),
        cuit: currentUser.entityInfo.cuit,
        maximum_weight: newCredentials?.maximum_weight
          ? Number(newCredentials?.maximum_weight)
          : null,
        maximum_price: newCredentials?.maximum_price
          ? Number(newCredentials?.maximum_price)
          : null,
        matrix: newCredentials.matrix,
        matrices: newCredentials.matrices,
        fallback_quotation: newCredentials.fallback_quotation,
        geographic_type: newCredentials.geographic_type ?? defaultGeoType,
      },
    });
  };

  const handleFileSelected = (file?: File) => {
    if (!file) {
      setMatrixFileErrors([]);
      return;
    }

    const errors = validateMatrixFile(file);
    if (errors.length > 0) {
      setMatrixFileErrors(errors);
      return;
    }

    setMatrixFileErrors([]);
    // Una matriz nueva siempre toma el tipo por defecto del país, no el heredado del switch
    handleChangeCredentials({
      ...ownFleetCarrier.credentials,
      matrix: file,
      geographic_type: defaultGeoType,
    });
  };

  async function handleDownload(matrix: IOwnLogisticsMatrix, isError: boolean) {
    const { url } = await getMatrixDownloadURL({
      marketplaceId: selectedMarketplace,
      matrixId: matrix._id,
      getError: isError,
    });
    const link = document.createElement("a");
    link.href = url;
    link.download = isError
      ? matrix.errors_aws_key?.split("/").pop() || "error.csv"
      : matrix.awsKey.split("/").pop() || "matrix.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
  const handleGetExampleMatrixURL = async () => {
    try {
      const { url } = await getMatrixDownloadURL({
        marketplaceId: selectedMarketplace,
        matrixId: EXAMPLE_MATRIX_ID,
        getError: false,
      });
      setExampleMatrixUrl(url);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    handleGetExampleMatrixURL();
  }, []);

  useEffect(() => {
    onMatrixValidationChange?.(matrixFileErrors.length > 0);
  }, [matrixFileErrors]);

  return (
    <Grid>
      <OwnFleetRulesModal
        open={showRulesModal}
        onClose={() => setShowRulesModal(false)}
        credentials={ownFleetCarrier.credentials}
        setCredentials={handleChangeCredentials}
      />
      {ownFleetCarrier.marketplace_merchant_apikey && (
        <OwnFleetApiKey apiKey={ownFleetCarrier.marketplace_merchant_apikey} />
      )}
      <Box className="bg-light br-10 m-t-40 p-20">
        <Grid container justifyContent="space-between" alignItems={"center"}>
          <Grid item padding={"0.688rem 1rem"}>
            <Typography className="f-s-16" style={{ fontWeight: "bold" }}>
              Flete Propio
            </Typography>
          </Grid>
          <Grid item justifyContent={"flex-end"}>
            <Grid container alignItems={"center"}>
              <Grid item padding={"0.688rem 1rem"}>
                <Typography
                  className="f-s-14 text-muted"
                  noWrap
                  sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
                >
                  <Box component="span" fontWeight="bold">
                    Procesamiento CSV:
                  </Box>{" "}
                  {csvStatusLabelMap[csvStatus].label}
                </Typography>
              </Grid>
              <Grid
                item
                display={"flex"}
                alignItems={"center"}
                padding={"0.688rem 1rem"}
              >
                <Button
                  disabled={
                    !(
                      ownFleetCarrier?.credentials?.matrices &&
                      ownFleetCarrier?.credentials?.matrices.length > 0
                    )
                  }
                  variant="text"
                  startIcon={<Settings className="text-muted" />}
                  className="text-muted"
                  sx={{ padding: 0 }}
                  onClick={() => setShowRulesModal(true)}
                >
                  Editar reglas
                </Button>
              </Grid>
              <Grid item padding={"0.688rem 1rem"}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={checked}
                      disabled={ownFleetCarrier.disabled && !canEnableOwnFleet}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setChecked(e.target.checked);
                        const activeMatrix =
                          ownFleetCarrier?.credentials?.matrices?.find(
                            (m: IOwnLogisticsMatrix) => m.active
                          );
                        handleCarriersChanges({
                          ...ownFleetCarrier,
                          disabled: !e.target.checked,
                          // Mantiene el tipo de la matriz vigente para que own-fleet no la invalide al cambiar solo el estado
                          credentials: {
                            ...ownFleetCarrier.credentials,
                            geographic_type: activeMatrix?.geographic_type,
                          },
                        });
                      }}
                    />
                  }
                  label={
                    <Typography className="f-s-14 text-muted">
                      {checked ? "Activo" : "Inactivo"}
                    </Typography>
                  }
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Divider className="m-t-20" />
        <Box className="bg-light">
          <Accordion
            expanded={expandAccordion}
            onChange={() => setExpandAccordion(!expandAccordion)}
            disableGutters
            elevation={0}
            square
            id="operators-accordion-main-row"
          >
            <AccordionSummary
              aria-controls="panel1d-content"
              id="panel1d-header"
              expandIcon={
                <span className="material-symbols-rounded f-s-24">
                  expand_more
                </span>
              }
            >
              <Typography pl={1}>Configurar matriz logística</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ paddingLeft: "3rem" }}>
              <Box display={"flex"} alignItems={"center"}>
                <IconButton size="small" color="primary">
                  <span className="material-symbols-rounded">info</span>
                </IconButton>
                <Typography className="f-s-14">
                  Para activar la opción de flete propio, primero asegurate de
                  que el archivo CSV esté subido y procesado correctamente.
                </Typography>
              </Box>
              <Box className="m-t-20">
                <Typography className="f-s-14 m-b-20">
                  Descarga la plantilla desde este{" "}
                  <Link
                    href={exampleMatrixUrl}
                    target="_blank"
                    rel="noreferrer"
                    download="matriz-ejemplo.csv"
                  >
                    enlace
                  </Link>{" "}
                  y completa los campos requeridos: códigos postales, peso
                  volumétrico (hasta dos decimales), días de entrega y precio
                  con IVA incluído.
                </Typography>
              </Box>
              <Box width={"50%"}>
                <Typography>Subi tu archivo CSV</Typography>
                <FileInput
                  fileInputRef={fileInputRef}
                  handleClick={handleClick}
                  errors={matrixFileErrors}
                  onFileSelected={handleFileSelected}
                  disabled={isProcessing}
                />
              </Box>
              {/* Al al menos 1 archivo procesado */}
              {currentMatrix.length > 0 && (
                <>
                  <Typography className="f-s-14 m-t-10">
                    Ultimo Archivo: {currentMatrix[0]?.name}
                  </Typography>
                  <Typography className="f-s-14 m-t-10">
                    Fecha de carga:{" "}
                    {mongoTimeStampToString(currentMatrix[0]?.created_at)}
                  </Typography>
                  <Grid display={"flex"} alignItems={"center"} gap={0.5}>
                    <Typography className="f-s-14 m-t-10">
                      Estado de carga:{" "}
                    </Typography>
                    <CSVUploadStatusLabel csvStatus={csvStatus} />
                  </Grid>
                </>
              )}

              {currentMatrix[0]?.processed && (
                <Button
                  size="medium"
                  variant="outlined"
                  className="m-t-10"
                  color="primary"
                  onClick={() =>
                    handleDownload(
                      currentMatrix[0],
                      !!currentMatrix[0]?.errors_aws_key
                    )
                  }
                  endIcon={<FileDownloadOutlinedIcon />}
                >
                  {currentMatrix[0].errors_aws_key
                    ? "Descargar errores"
                    : "Descargar matriz"}
                </Button>
              )}
            </AccordionDetails>
          </Accordion>
        </Box>
      </Box>
    </Grid>
  );
};
